/**
 * Page formateur : saisie des notes de devoirs.
 * Sélection (classe, matière, semestre) → tableau étudiants avec D1, D2.
 * Bloquée si la scolarité a fermé la fenêtre de saisie.
 */
import React, { useEffect, useState } from 'react';
import { PenTool, Lock, Loader2, GraduationCap } from 'lucide-react';
import {
  useMesMatieresQuery, useListerSaisieQuery, useSaveNoteMutation,
} from '../../services/formateurNotesApi';
import type { EtuRow } from '../../services/formateurNotesApi';

export default function SaisieNotesFormateur() {
  const [classe, setClasse] = useState('');
  const [matiere, setMatiere] = useState('');
  const [semestre, setSemestre] = useState(1);

  const { data: matieres, isFetching: loadingMat } = useMesMatieresQuery();
  const { data, isFetching, refetch } = useListerSaisieQuery(
    { classe, matiere, semestre },
    { skip: !classe || !matiere },
  );
  const [saveNote] = useSaveNoteMutation();

  const handleNote = async (
    etu: EtuRow, champ: 'note1' | 'note2', valeur: string | null, absent: boolean,
  ) => {
    try {
      await saveNote({
        matricule: etu.matricule, champ, valeur, absent,
        classe, matiere, semestre,
      }).unwrap();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <PenTool size={24} /> Saisie des notes de devoirs
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Saisissez D1 et D2 pour les étudiants des matières dont vous êtes l'enseignant déclaré.
          La saisie nécessite une fenêtre ouverte par la scolarité.
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3 mb-4 flex flex-wrap gap-3 items-center">
        <GraduationCap size={18} className="text-gray-400" />
        {loadingMat ? <span className="text-xs text-gray-500">Chargement…</span> : (
          <>
            <select value={`${classe}|${matiere}`}
              onChange={(e) => {
                const [c, m] = e.target.value.split('|');
                setClasse(c || ''); setMatiere(m || '');
              }}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
              <option value="|">— Choisir une matière —</option>
              {(matieres?.results || []).map((m, i) => (
                <option key={i} value={`${m.classe}|${m.matiere_libelle}`}>
                  {m.classe} — {m.matiere_libelle}
                </option>
              ))}
            </select>
            <select value={semestre} onChange={(e) => setSemestre(Number(e.target.value))}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
              <option value={1}>Semestre 1</option>
              <option value={2}>Semestre 2</option>
            </select>
            {classe && matiere && (
              <button onClick={() => refetch()}
                className="ml-auto px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded">
                {isFetching ? '…' : '↻'}
              </button>
            )}
          </>
        )}
      </div>

      {!classe || !matiere ? (
        <div className="text-center text-gray-500 dark:text-gray-400 text-sm py-12">
          Sélectionnez une matière pour commencer.
        </div>
      ) : isFetching && !data ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin text-blue-500" size={28} />
        </div>
      ) : data ? (
        <>
          {!data.fenetre_ouverte && (
            <div className="mb-3 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 rounded p-3 flex items-center gap-2">
              <Lock size={18} />
              <div>
                <div className="font-semibold">Fenêtre de saisie fermée</div>
                <div className="text-xs">La scolarité a verrouillé la saisie. Contactez-les pour faire ouvrir une période.</div>
              </div>
            </div>
          )}

          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">Étudiant</th>
                  <th className="px-3 py-2 text-center text-[10px] font-semibold text-gray-500 uppercase w-32">D.1</th>
                  <th className="px-3 py-2 text-center text-[10px] font-semibold text-gray-500 uppercase w-32">D.2</th>
                  <th className="px-3 py-2 text-center text-[10px] font-semibold text-gray-500 uppercase w-20">Moyenne</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {data.etudiants.map(e => (
                  <RowEtudiant key={e.matricule} etu={e}
                    disabled={!data.fenetre_ouverte || e.verrouille}
                    onSave={handleNote} />
                ))}
              </tbody>
            </table>
            {data.etudiants.length === 0 && (
              <div className="p-8 text-center text-gray-500">Aucun étudiant dans cette classe.</div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}

function RowEtudiant({ etu, disabled, onSave }: {
  etu: EtuRow; disabled: boolean;
  onSave: (e: EtuRow, c: 'note1'|'note2', v: string|null, abs: boolean) => Promise<void>;
}) {
  return (
    <tr className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors ${disabled ? 'opacity-60' : ''}`}>
      <td className="px-3 py-2">
        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {etu.prenoms} {etu.nom}
          {etu.verrouille && <Lock size={12} className="inline ml-1 text-amber-500" />}
        </div>
        <div className="text-[10px] text-gray-500 font-mono">{etu.matricule}</div>
      </td>
      <CelluleNote etu={etu} champ="note1" valeur={etu.note1} absent={etu.absent1}
        disabled={disabled} onSave={onSave} />
      <CelluleNote etu={etu} champ="note2" valeur={etu.note2} absent={etu.absent2}
        disabled={disabled} onSave={onSave} />
      <td className="px-3 py-2 text-center">
        {etu.moyenne ? (
          <span className={`font-bold text-sm ${Number(etu.moyenne) >= 10 ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
            {Number(etu.moyenne).toFixed(2)}
          </span>
        ) : <span className="text-xs text-gray-400">—</span>}
      </td>
    </tr>
  );
}

function CelluleNote({ etu, champ, valeur, absent, disabled, onSave }: {
  etu: EtuRow; champ: 'note1'|'note2'; valeur: string | null; absent: boolean; disabled: boolean;
  onSave: (e: EtuRow, c: 'note1'|'note2', v: string|null, abs: boolean) => Promise<void>;
}) {
  const [val, setVal] = useState(valeur || '');
  const [abs, setAbs] = useState(absent);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setVal(valeur || ''); setAbs(absent); }, [valeur, absent]);

  const handleBlur = async () => {
    if (val === (valeur || '') && abs === absent) return;
    setSaving(true);
    try { await onSave(etu, champ, val || null, abs); }
    finally { setSaving(false); }
  };

  return (
    <td className="px-3 py-2 text-center">
      {abs ? (
        <button onClick={async () => { setAbs(false); await onSave(etu, champ, val || null, false); }}
          disabled={disabled}
          className="px-2 py-1 text-[10px] bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300 rounded">
          Absent
        </button>
      ) : (
        <div className="inline-flex items-center gap-1">
          <input type="number" min={0} max={20} step={0.25}
            value={val} onChange={(e) => setVal(e.target.value)} onBlur={handleBlur}
            disabled={disabled || saving}
            className="w-16 px-2 py-1 text-sm text-center border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100" />
          <button type="button" onClick={async () => { setAbs(true); await onSave(etu, champ, null, true); }}
            disabled={disabled || saving} title="Marquer absent"
            className="px-1.5 py-1 text-[10px] text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/30 rounded">
            Abs
          </button>
          {saving && <Loader2 size={11} className="animate-spin text-blue-500" />}
        </div>
      )}
    </td>
  );
}
